import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";

const Link = ({ href, children }) => (
  <a href={href} target="_blank" rel="noopener noreferrer" className="text-gold hover:underline hover:drop-shadow-[0_0_6px_rgba(201,168,76,0.4)] transition-all">{children}</a>
);

const faqs = [
  {
    q: "What is an EHO inspection and how often does it happen?",
    a: <>An Environmental Health Officer (EHO) inspection is an unannounced visit by your local authority to assess food hygiene and safety standards. Inspections typically happen every 6 months to 2 years depending on your current hygiene rating. Businesses rated 0–2 are inspected more frequently, while those rated 4–5 may see longer gaps between visits. See the <Link href="https://www.food.gov.uk/safety-hygiene/food-hygiene-rating-scheme">Food Hygiene Rating Scheme</Link> for more details.</>,
  },
  {
    q: "What do EHO inspectors actually look for?",
    a: <>Inspectors assess three core areas: hygienic food handling (preparation, cooking, reheating, cooling, and storage), physical condition of the premises (cleanliness, layout, ventilation, pest control), and food safety management (your HACCP system, documented procedures, temperature logs, allergen records, and staff training evidence). Each area is scored and combined into your overall hygiene rating. Full guidance is available from the <Link href="https://www.food.gov.uk/business-guidance">Food Standards Agency</Link>.</>,
  },
  {
    q: "What is HACCP and do I legally need it?",
    a: <>HACCP stands for Hazard Analysis and Critical Control Points. It's a systematic approach to identifying and controlling food safety hazards. Under UK law (<Link href="https://www.legislation.gov.uk/european/regulation/2004/0852">Regulation EC 852/2004</Link> and the <Link href="https://www.legislation.gov.uk/ukpga/1990/16/contents">Food Safety Act 1990</Link>), every food business is legally required to have food safety procedures based on HACCP principles. This isn't optional — operating without one puts your business at serious legal risk.</>,
  },
  {
    q: "What's the difference between a generic template and a tailored HACCP system?",
    a: "Generic templates cover broad food safety principles but don't reflect your specific kitchen, menu, equipment, or processes. A tailored HACCP system is built around your exact operation — your critical control points, your workflows, your risks. EHO inspectors can spot generic documents immediately, and they won't score as highly as a system that clearly relates to your business.",
  },
  {
    q: "How quickly can I improve my hygiene rating?",
    a: <>With the right systems in place, significant improvement is achievable before your next inspection. Our Audit + Action Plan package gives you a clear, step-by-step roadmap to reach a 4–5 rating. You can check your current rating on the <Link href="https://ratings.food.gov.uk/">FHRS website</Link>.</>,
  },
  {
    q: "What are the 14 allergens I need to declare?",
    a: <>UK law requires you to declare 14 allergens: celery, cereals containing gluten, crustaceans, eggs, fish, lupin, milk, molluscs, mustard, nuts, peanuts, sesame, soya, and sulphur dioxide (sulphites). You must be able to tell customers which allergens are present in every dish. Full allergen guidance is available from the <Link href="https://www.food.gov.uk/business-guidance/allergen-guidance-for-food-businesses">Food Standards Agency allergen page</Link>.</>,
  },
  {
    q: "Do I need to keep temperature logs every day?",
    a: <>Yes. Temperature control sits at the heart of every HACCP system. Fridges, freezers, cooking, reheating — every process has a legal safe zone. Without a daily log, you can't prove you hit it. Our pre-formatted temperature logs include out-of-range action columns and monthly summary sheets to make this straightforward for your team. See <Link href="https://www.food.gov.uk/business-guidance/safer-food-better-business">Safer Food, Better Business</Link> for FSA guidance.</>,
  },
  {
    q: "What happens if I fail an EHO inspection?",
    a: <>Depending on the severity, outcomes range from a low hygiene rating (publicly visible on the <Link href="https://ratings.food.gov.uk/">Food Hygiene Rating Scheme</Link>) to formal written warnings, improvement notices requiring action within a set timeframe, emergency closure if there's an imminent risk to health, or prosecution under the <Link href="https://www.legislation.gov.uk/ukpga/1990/16/contents">Food Safety Act 1990</Link>. A failed inspection also damages customer trust and can significantly impact revenue.</>,
  },
];

export default function FAQ() {
  return (
    <section
      data-testid="faq-section"
      className="relative py-24 md:py-32 lg:py-40 bg-[#0d0d0d]/[0.93]"
    >
      <div className="max-w-3xl mx-auto px-6 md:px-12 lg:px-24">
        <div className="text-center mb-14">
          <div className="gold-line mx-auto mb-6" />
          <span className="text-xs font-semibold uppercase tracking-[0.2em] text-gold">
            Common Questions
          </span>
          <h2
            data-testid="faq-heading"
            className="font-heading text-3xl md:text-4xl font-medium text-white mt-4"
          >
            EHO & HACCP
            <br />
            <span className="text-zinc-500">Frequently Asked Questions</span>
          </h2>
        </div>

        <Accordion type="single" collapsible className="space-y-3">
          {faqs.map((faq, i) => (
            <AccordionItem
              key={i}
              value={`faq-${i}`}
              data-testid={`faq-item-${i}`}
              className="gold-card px-6 border-none"
            >
              <AccordionTrigger
                data-testid={`faq-trigger-${i}`}
                className="text-left text-sm md:text-base text-white hover:text-gold py-5 hover:no-underline [&[data-state=open]]:text-gold"
              >
                {faq.q}
              </AccordionTrigger>
              <AccordionContent
                data-testid={`faq-content-${i}`}
                className="text-sm text-zinc-400 leading-relaxed pb-5"
              >
                {faq.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  );
}
